import clsx from 'clsx'
import { useController, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import PlusIcon from '@/assets/icons/add.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import MoonIcon from '@/assets/icons/moon.svg?react'
import ButtonIcon from '@/components/ButtonIcon/ButtonIcon'
import Checkbox from '@/components/Checkbox/Checkbox'
import FormField from '@/components/FormField'
import Switch from '@/components/Switch/Switch'
import TimeRangePicker from '@/components/TimePicker/TimeRangePicker'
import type {
  AvailabilityForm,
  AvailabilityFormDaySlot
} from '@/constants/availability-form'
import type { DayOfWeek } from '@/constants/days-of-week'
import { useTimetableSetttings } from '@/modules/timetable'
import type { Form } from '@/types/generic-form'

import styles from './AvailabilityDayField.module.scss'

type AvailabilityDayFieldProps<T extends AvailabilityForm> = {
  form: Form<AvailabilityForm, T>
  day: DayOfWeek
  appendOnSwitchOn: boolean
}

const defaultDaySlotValue: AvailabilityFormDaySlot = {
  time: {
    from: undefined,
    to: undefined
  },
  isOptional: false
}

const AvailabilityDayField = <T extends AvailabilityForm>(
  props: AvailabilityDayFieldProps<T>
) => {
  const { t } = useTranslation('common')

  const form = props.form

  const { fields, replace, insert, remove } = useFieldArray({
    control: form.control,
    name: props.day
  })

  const handleSwitchChange = (value: boolean) => {
    if (value) {
      if (props.appendOnSwitchOn) replace([{ ...defaultDaySlotValue }])
      else form.resetField(props.day)
    } else replace([])
  }

  const {
    fieldState: { error }
  } = useController({
    name: props.day,
    control: form.control
  })

  return (
    <div
      className={styles.dayAvailability}
      onChange={() => form.clearErrors(props.day)}
    >
      <Switch
        id={`enable-availability-for-${props.day}`}
        value={!!fields.length}
        onChange={handleSwitchChange}
        label={t(`days-of-week.full.${props.day}`)}
        className={styles.daySwitch}
      />
      {fields.length ? (
        <div className={styles.daySlots}>
          {fields.map((slot, index) => (
            <Slot
              form={form}
              index={index}
              onAdd={() => insert(index + 1, { ...defaultDaySlotValue })}
              onRemove={() => remove(index)}
              key={slot.id}
              day={props.day}
              withDelete={fields.length > 1}
            />
          ))}
        </div>
      ) : (
        <Unavailable />
      )}
      <span className={styles.errorText}>{error?.message}</span>
    </div>
  )
}

type SlotProps<T extends AvailabilityForm> = {
  form: Form<AvailabilityForm, T>
  index: number
  day: DayOfWeek
  onAdd: () => void
  withDelete: boolean
  onRemove: () => void
}

const Slot = <T extends AvailabilityForm>(props: SlotProps<T>) => {
  const { t } = useTranslation('common')

  const { data: schoolWorkingHours } = useTimetableSetttings()

  return (
    <div className={styles.slot}>
      <FormField
        id={`${props.day}-${props.index}-optional`}
        control={props.form.control}
        name={`${props.day}.${props.index}.isOptional`}
        render={({ inputProps }) => (
          <Checkbox
            {...inputProps}
            onChange={value => {
              inputProps.onChange?.(value)
              // https://github.com/react-hook-form/resolvers/issues/745
              // trigger validation for every field that already has an error
              props.form.formState.errors[props.day]?.forEach?.(
                (slot, index) => {
                  if (slot?.time)
                    props.form.trigger(`${props.day}.${index}.time`)
                }
              )
            }}
            label={t('label.optional')}
          />
        )}
      />
      <div className={styles.editSlot}>
        <FormField
          id={`${props.day}.${props.index}.time`}
          control={props.form.control}
          name={`${props.day}.${props.index}.time`}
          className={styles.timeRangePicker}
          render={({ inputProps }) => (
            <TimeRangePicker
              {...inputProps}
              min={schoolWorkingHours?.startTime}
            />
          )}
        />
        <div className={styles.slotButtons}>
          <ButtonIcon
            ariaLabel={t('button.add-slot')}
            variant="secondary"
            size="medium"
            onClick={props.onAdd}
            className={clsx(!props.withDelete && styles.withoutDelete)}
          >
            <PlusIcon />
          </ButtonIcon>
          {props.withDelete ? (
            <ButtonIcon
              ariaLabel={t('button.remove-slot')}
              size="medium"
              variant="tertiary"
              onClick={props.onRemove}
            >
              <DeleteIcon />
            </ButtonIcon>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const Unavailable = () => {
  const { t } = useTranslation('common')

  return (
    <div className={styles.unavailable}>
      <MoonIcon />
      {t('label.unavailable')}
    </div>
  )
}

export default AvailabilityDayField
